'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { m as motion, LazyMotion, domAnimation } from 'motion/react';
import Image from 'next/image';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { Palette, CheckCircle2, Globe, ChevronDown, Code2, PhoneCall } from 'lucide-react';
import ProjectEstimator from './ProjectEstimator';

const faqs = [
  {
    question: 'How long does it take to build a custom website?',
    answer:
      'The timeline depends on the complexity and scope of your project. A standard business website typically takes 4-6 weeks from initial design to launch. We prioritize transparency and keep you updated every step of the way to ensure a timely delivery.',
  },
  {
    question: 'Why should I hire a web design agency in Hawaii instead of an agency overseas?',
    answer:
      'Working with a web design agency in Hawaii like Zumify ensures clear communication without extreme timezone delays. We understand the local market, the culture, and what appeals to both residents and tourists, empowering us to build a website that truly resonates with your target audience.',
  },
  {
    question: 'Will my website be optimized for mobile devices?',
    answer:
      'Absolutely. With mobile traffic making up a significant portion of web visits in Hawaii, we ensure every site we build is fully responsive. Your website will look and function seamlessly on smartphones, tablets, and desktops alike.',
  },
  {
    question: 'How do you help my website rank on Google?',
    answer:
      'We build SEO best practices into the core foundation of your website. From semantic HTML and incredibly fast Next.js load times to keyword optimization and local Hawaii SEO strategies, we ensure your site is built to rank highly on search engines and attract local leads.',
  },
  {
    question: 'Do you provide website maintenance and hosting?',
    answer:
      'Yes, we handle the entire technical setup. We offer fast, secure, and reliable hosting services, along with ongoing maintenance packages to keep your website running smoothly 24/7, allowing you to focus completely on your business.',
  },
];

function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-white/10">
      <button
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors group"
        onClick={onClick}
      >
        <span className="text-xl sm:text-2xl font-semibold text-white group-hover:text-purple-300 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-purple-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-stone-400 text-lg leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

export function ResponsiveWebsite() {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  // Reusing established homepage animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulseSlow = {
    animate: {
      opacity: [0.3, 0.5, 0.3],
      scale: [1, 1.1, 1],
      transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
        <ResponsiveHeader />

        {/* Hero / Information Section */}
        <section className="relative min-h-[60vh] flex items-center pt-32 lg:pt-56 pb-20">
          {/* Background Image Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-zinc-900/80 sm:bg-zinc-900/70 z-10" />{' '}
            {/* Dark Overlay for text readability */}
            {/* Gradient mask to fade bottom edge into the next section */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-zinc-900 to-transparent z-10" />
            <img
              src="/images/responsive-hero.webp"
              alt="Responsive Web Design across Devices"
              className="w-full h-full object-cover object-center sm:object-top opacity-50"
            />
          </div>

          {/* Animated Glow Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <motion.div
              className="absolute top-20 left-10 w-72 h-72 bg-purple-600/30 rounded-full blur-[100px]"
              variants={pulseSlow}
              animate="animate"
            />
            <motion.div
              className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-600/30 rounded-full blur-[100px]"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
                transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          </div>

          <div className="relative w-full px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center z-10">
            <div className="max-w-4xl mx-auto backdrop-blur-sm sm:backdrop-blur-none bg-zinc-900/40 sm:bg-transparent p-6 rounded-3xl">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-8"
              >
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] drop-shadow-2xl"
                >
                  Web Design Agency <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 drop-shadow-md">
                    Hawaii
                  </span>
                </motion.h1>

                <motion.p
                  variants={fadeInUp}
                  className="text-lg sm:text-lg md:text-xl text-stone-200 lg:text-stone-300 max-w-2xl mx-auto leading-relaxed drop-shadow-xl font-medium"
                >
                  Looking for a top-rated web design agency in Hawaii? Zumify creates sleek, fast,
                  and conversion-focused websites tailored to local businesses across Oahu, Maui,
                  Kauai, and the Big Island. Whether you're launching a new brand or redesigning an
                  existing site, the Zumify team delivers results-driven web design that looks
                  stunning and performs even better.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Product Information / Impact Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20 pt-10 lg:pt-20 relative">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Our Web Design Services
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
              <p className="mt-6 text-lg text-stone-300 max-w-2xl mx-auto">
                Zumify offers a full suite of web design and development services for Hawaii
                businesses of all sizes. Every project is customized to your goals, your audience,
                and your brand.
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-12 lg:space-y-16 mt-16">
              {/* Text Block 1 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 p-0 h-full bg-gradient-to-b from-cyan-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Sleek, Modern & Responsive Design
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  We build websites that look as good on a phone as they do on a widescreen monitor.
                  Our designs are clean, contemporary, and thoughtfully crafted to reflect your
                  brand's identity. With Hawaii's high mobile usage rates, a responsive site is
                  essential to reaching your customers wherever they are.
                </p>
              </motion.div>

              {/* Text Block 2 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-purple-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  SEO Optimization to Rank Higher on Google
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  A beautiful website is worthless if no one can find it. That's why SEO is baked
                  into every site we build. From proper HTML structure, semantic markup, and fast
                  load times to keywords, meta tags, and local SEO strategies for Hawaii. We make
                  sure your site is set up to rank.
                </p>
              </motion.div>

              {/* Text Block 3 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-pink-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Conversion-Optimized Calls to Action (CTAs)
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  Getting visitors to your site is only half the battle. We design and strategically
                  place calls to action that turn browsers into buyers, leads, and loyal customers.
                  From the placement of buttons and forms to the language we use and the visual
                  hierarchy we create, every CTA on your site is intentional. We also value quality
                  leads with the use of technology like ReCaptcha and Google Scripts. We help Hawaii
                  businesses turn their website into a 24/7 sales and lead generation tool.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Solutions & Capabilities Section - Picture + Text */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* Text Side */}
              <motion.div
                className="w-full lg:w-1/2 space-y-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
              >
                <div className="space-y-4">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                    Why Choose Zumify As Your
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                      Web Design Agency in Hawaii
                    </span>
                  </h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full" />
                </div>

                <p className="text-lg text-stone-300 leading-relaxed">
                  Hawaii businesses deserve more than a cookie-cutter template. Zumify combines the
                  technical expertise of a full-stack development team with a designer's eye for
                  detail and a marketer's understanding of what drives conversions. We're not just
                  building websites, we're building growth engines for Hawaii businesses.
                </p>

                <ul className="space-y-4">
                  {[
                    'Unique & Eye Catching Design',
                    'Rank High to Get Natural Leads',
                    'Conversion Optimized CTA',
                    'Custom Client Portals & Dashboards',
                  ].map((item, idx) => (
                    <motion.li
                      key={idx}
                      className="flex items-center gap-3 text-stone-200"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + idx * 0.1 }}
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                      </div>
                      <span className="text-lg">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Picture Side */}
              <motion.div
                className="w-full lg:w-1/2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
              >
                <div className="relative aspect-video rounded-3xl bg-zinc-900 border border-white/10 shadow-2xl shadow-purple-500/20 overflow-hidden group">
                  {/* Decorative background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 pointer-events-none" />

                  {/* Video Showcase */}
                  <div className="absolute inset-0 p-2 sm:p-4 lg:p-6 flex items-center justify-center pointer-events-none">
                    <div className="w-full h-full rounded-2xl md:rounded-[20px] bg-zinc-800/50 backdrop-blur-xl border border-white/10 overflow-hidden shadow-2xl relative group-hover:scale-[1.02] transition-transform duration-700 pointer-events-auto">
                      <video
                        src="/videos/HRUDesktop.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-contain bg-zinc-900 rounded-xl md:rounded-[18px]"
                      />
                      {/* Subtle gradient overlay to blend corners nicely */}
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/40 via-transparent to-transparent pointer-events-none rounded-xl md:rounded-[18px]" />
                    </div>
                  </div>

                  {/* Top-right corner accent */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl pointer-events-none" />
                  {/* Bottom-left corner accent */}
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 blur-3xl pointer-events-none" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {/* 4-Step Process Section - Scrolling Banners */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 bg-zinc-800/30 overflow-hidden relative border-y border-white/5">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-24"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Our Website Design{' '}
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  Process
                </span>
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-24 lg:space-y-40">
              {[
                {
                  step: '01',
                  icon: <PhoneCall className="w-10 h-10 text-blue-400" />,
                  title: 'Intro Call and Meeting',
                  description:
                    "We start by learning about your business, your goals, and your target audience. During our introductory call or meeting, we'll discuss your vision for the website and outline a clear plan of action to ensure the final product perfectly aligns with your needs.",
                  color: 'blue',
                  bgColor: 'from-blue-500 to-transparent',
                  borderColor: 'border-blue-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(59,130,246,0.3)]',
                  image: '/images/intro-meeting.webp',
                },
                {
                  step: '02',
                  icon: <Palette className="w-10 h-10 text-cyan-400" />,
                  title: 'Figma Design & Layout',
                  description:
                    "Before we write a single line of code, we design your website in Figma: a professional design tool that lets us create high-fidelity mockups of every page. You'll be able to see exactly how your website will look, review it, request changes, and approve the design before development begins. This step ensures we're aligned on the vision and eliminates costly revisions down the road.",
                  color: 'cyan',
                  bgColor: 'from-cyan-500 to-transparent',
                  borderColor: 'border-cyan-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(34,211,238,0.3)]',
                  image: '/images/figma-step.webp',
                },
                {
                  step: '03',
                  icon: <Code2 className="w-10 h-10 text-purple-400" />,
                  title: 'Development in Next.js',
                  description:
                    'Once the design is approved, we bring it to life using Next.js: one of the most powerful and modern web frameworks available today. Next.js delivers blazing-fast performance, excellent SEO capabilities through server-side rendering, and a rock-solid foundation for scaling your site as your business grows. Your Hawaii website will be built to the highest development standards.',
                  color: 'purple',
                  bgColor: 'from-purple-500 to-transparent',
                  borderColor: 'border-purple-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]',
                  image: '/images/development.webp',
                },
                {
                  step: '04',
                  icon: <Globe className="w-10 h-10 text-pink-400" />,
                  title: 'Hosting on Your Website',
                  description:
                    'When your site is ready, we launch it on a domain of your choice. Whether you already have a domain or need help selecting one, we handle the deployment and make sure your website goes live smoothly. We configure everything properly: DNS, SSL certificates, and performance settings so your site is secure, fast, and professional from day one.',
                  color: 'pink',
                  bgColor: 'from-pink-500 to-transparent',
                  borderColor: 'border-pink-500/30',
                  shadow: 'shadow-[0_0_30px_rgba(236,72,153,0.3)]',
                  image: '/images/458aa8e102e503d704ce969c4eb04d36b19b6cc3.webp',
                },
              ].map((step, idx) => {
                const isEven = idx % 2 === 1;

                return (
                  <div
                    key={idx}
                    className="relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20"
                  >
                    {/* Text Side */}
                    <motion.div
                      className={`w-full lg:w-1/2 space-y-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                      variants={isEven ? fadeInRight : fadeInLeft}
                    >
                      <div className="flex items-center gap-6 mb-2">
                        <span
                          className={`text-6xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.bgColor} opacity-50`}
                        >
                          {step.step}
                        </span>
                        <div
                          className={`p-4 rounded-2xl bg-zinc-800 border ${step.borderColor} shadow-lg`}
                        >
                          {step.icon}
                        </div>
                      </div>

                      <h3 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                        {step.title}
                      </h3>

                      <p className="text-lg text-stone-300 leading-relaxed max-w-xl">
                        {step.description}
                      </p>
                    </motion.div>

                    {/* Image / Visual Side */}
                    <motion.div
                      className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                      variants={isEven ? fadeInLeft : fadeInRight}
                    >
                      <div
                        className={`relative w-full aspect-[4/3] rounded-[24px] bg-zinc-900 border-2 ${step.borderColor} ${step.shadow} overflow-hidden group`}
                      >
                        {'image' in step && step.image ? (
                          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden flex items-center justify-center p-6">
                            <img
                              src={step.image}
                              alt={step.title}
                              className="w-full h-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-700 relative z-10 shadow-xl shadow-black/50"
                            />
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center p-8">
                            <div
                              className={`w-full h-full border border-white/5 rounded-2xl bg-gradient-to-br ${step.bgColor} backdrop-blur-3xl flex items-center justify-center group-hover:scale-105 transition-transform duration-700`}
                            >
                              {/* Decorative element acting as an image placeholder */}
                              <div className="w-32 h-32 rounded-full bg-white/5 backdrop-blur-md flex items-center justify-center border border-white/10">
                                {step.icon}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 bg-zinc-900/50 relative z-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Answers to Your Questions About Web Design
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Portfolio Previews / Results */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Text / Results Side */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                className="space-y-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-sm font-semibold">
                  PROVEN RESULTS
                </div>

                <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                  High Performance <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                    Modern Frameworks
                  </span>
                </h2>

                <p className="text-lg text-stone-300 leading-relaxed">
                  We don't rely on bloated templates. Every site we build is natively coded to
                  guarantee sub-second load times, flawless responsive behavior across all Apple and
                  Android devices, and robust SEO metrics right out of the box.
                </p>

                <div className="space-y-4 pt-4">
                  {[
                    'Custom React / Next.js architecture',
                    'Framer Motion interactive animations',
                    'Advanced on-page technical SEO',
                    'Conversion-optimized responsive layouts',
                  ].map((item, idx) => (
                    <motion.div key={idx} className="flex items-center gap-3" variants={fadeInLeft}>
                      <CheckCircle2 className="w-6 h-6 text-purple-400 flex-shrink-0" />
                      <span className="text-stone-300 font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>

                <motion.div className="grid grid-cols-2 gap-4 pt-6" variants={fadeInUp}>
                  <div className="p-6 rounded-2xl bg-zinc-800/50 border border-white/5 backdrop-blur-sm">
                    <div className="text-3xl font-bold text-cyan-400 mb-1">99+</div>
                    <div className="text-sm text-stone-400">Google Lighthouse Score</div>
                  </div>
                  <div className="p-6 rounded-2xl bg-zinc-800/50 border border-white/5 backdrop-blur-sm">
                    <div className="text-3xl font-bold text-purple-400 mb-1">&lt; 1s</div>
                    <div className="text-sm text-stone-400">Average Load Time</div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Visual Mockup Side */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                className="relative"
              >
                {/* Decorative Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-[80px] -z-10" />

                <div className="relative w-full aspect-video rounded-[24px] bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 p-[2px] shadow-[0_0_40px_rgba(168,85,247,0.4)] group">
                  <div className="w-full h-full rounded-[22px] bg-zinc-900 overflow-hidden relative flex items-center justify-center p-2 sm:p-4">
                    <video
                      src="/videos/zumifyTemplates.mp4"
                      className="w-full h-full object-contain bg-zinc-900 rounded-xl opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-[1.02]"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent pointer-events-none rounded-[22px]" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 bg-zinc-900 border-t border-white/5 relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                What Our Clients Say
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {[
                {
                  text: (
                    <>
                      "
                      <span className="text-cyan-300">
                        Zumify put together a beautiful new website
                      </span>{' '}
                      for my probate business, which I believe has been helpful in attracting new
                      leads and clients. They are professional, quick to respond to all inquiries
                      and requests, courteous and caring. I was most impressed with the use of the
                      newest techniques in design and website construction."
                    </>
                  ),
                  author: 'Victoria, KPRESS Law',
                  role: 'Probate & Estate Planning',
                  delay: 0,
                },
                {
                  text: (
                    <>
                      "
                      <span className="text-purple-300">
                        Hawaii Rugby Union administration would like to thank Wolfgang Sakamaki
                      </span>{' '}
                      for his vision, graphics and timely work on our website. A 5 star rating. Our
                      website is now precisely what is needed to showcase Hawaii to the Global Rugby
                      market."
                    </>
                  ),
                  author: 'Hawaii Rugby Union',
                  role: 'Global Rugby Organization',
                  delay: 0.2,
                },
              ].map((review, index) => (
                <motion.div
                  key={index}
                  className="text-center bg-zinc-800/30 rounded-3xl p-8 border border-white/5 backdrop-blur-sm hover:border-purple-500/30 transition-colors duration-500"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: review.delay }}
                >
                  <div className="flex justify-center gap-2 mb-8">
                    {[...Array(5)].map((_, i) => (
                      <motion.svg
                        key={i}
                        className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 fill-current drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                        viewBox="0 0 24 24"
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + i * 0.1, type: 'spring' }}
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </motion.svg>
                    ))}
                  </div>

                  <motion.blockquote
                    className="text-lg font-medium text-white mb-10 leading-normal"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + review.delay }}
                  >
                    {review.text}
                  </motion.blockquote>

                  <motion.div
                    className="flex flex-col items-center gap-2"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 + review.delay }}
                  >
                    <cite className="not-italic text-lg font-bold text-white">{review.author}</cite>
                    <span className="text-stone-400">{review.role}</span>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* Third review — full width */}
            <motion.div
              className="mt-8 lg:mt-10 text-center bg-zinc-800/30 rounded-3xl p-8 border border-white/5 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              {/* Stars */}
              <div className="flex justify-center gap-2 mb-8">
                {[...Array(5)].map((_, i) => (
                  <motion.svg
                    key={i}
                    className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 fill-current drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                    viewBox="0 0 24 24"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1, type: 'spring' }}
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </motion.svg>
                ))}
              </div>

              {/* Full quote */}
              <motion.blockquote
                className="text-lg font-medium text-white mb-10 leading-relaxed max-w-4xl mx-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                &ldquo;We&rsquo;re a local mom-and-pop business, and for a long time, our website
                was something we knew needed help but didn&rsquo;t quite know where to start. It was
                outdated, a little disorganized, and honestly wasn&rsquo;t doing much for us online.{' '}
                <span className="text-purple-300">
                  Working with Zumify made the whole process feel easy.
                </span>{' '}
                Their team was patient, helpful, and genuinely cared about helping us create a
                website that better represents who we are and attracts more customers. They showed
                aloha from start to finish, and we felt supported every step of the way. I was
                honestly beyond impressed with how everything turned out. They&rsquo;re a small
                team, but they work hard, move quickly, and pay attention to the details. Our
                website was finished in less than two weeks, and the final result was better than we
                could have imagined.{' '}
                <span className="text-purple-300">We&rsquo;re so grateful we found Zumify</span> and
                would highly recommend them to any small business looking to improve their online
                presence.&rdquo;
              </motion.blockquote>

              {/* Client Info */}
              <motion.div
                className="flex flex-col items-center gap-2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
              >
                <cite className="not-italic text-lg font-bold text-white">Waiola Shave Ice</cite>
                <span className="text-stone-400">Shave Ice Business, Hawaiʻi</span>
              </motion.div>
            </motion.div>

            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <a
                href="https://g.page/r/CQ21vPgDDtzxEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-stone-300 hover:text-white transition-colors group"
              >
                <span className="border-b border-transparent group-hover:border-white transition-all">
                  See our reviews on Google
                </span>
                <svg
                  className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Website Pricing Tiers Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 bg-zinc-900/50 relative z-20">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Website Pricing{' '}
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Tiers
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-stone-300 max-w-3xl mx-auto">
                Explore our Price Tiers of the products we craft for Hawaii's businesses.
                <br className="hidden sm:block" />
                <span className="text-stone-400 text-sm sm:text-base mt-2 block">
                  Note: All quotes will be finalized based on complexity, time, and landscape of
                  project proposal. Minimums enforced.
                </span>
              </p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8 flex justify-center"
              >
                <button
                  onClick={() => setIsEstimatorOpen(true)}
                  className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  Try Our Project Estimator
                </button>
              </motion.div>
            </motion.div>

            <div className="space-y-12">
              {/* Websites Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border border-cyan-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left Panel */}
                  <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                    {/* Poster Background */}
                    <Image
                      src="/images/seoServiceZumify.webp"
                      alt="Websites Tier Poster"
                      fill
                      className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                    />
                    {/* Decorative Background for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                    <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-cyan-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                    <div className="relative z-10 mt-auto">
                      <h3 className="text-2xl font-bold text-white mb-2">Websites</h3>
                      <div className="space-y-1">
                        <p className="text-sm text-stone-400">Starter Plan</p>
                        <p className="text-sm text-stone-400">Conversion Plan</p>
                        <p className="text-sm text-stone-400">Custom Plan</p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tier 1 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">
                        Starter Plan
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Custom-designed, animated website (fully responsive)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>3-5 core pages (Home, About, Services, Contact)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Basic SEO setup (meta tags, indexing, site structure)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Standard integrations (email, forms, scheduling like Calendly)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Redesign Service for Wix, Sqaurespace, Shopify, Wordpress</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Hourly Rate</p>
                          <p className="text-xl font-bold text-cyan-400">$75 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">20-50</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$1,500 - $3,750</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Starter Plan</p>
                          <p className="text-cyan-400">$49.99 / mo</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 2 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                        Conversion Plan
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Conversion-focused UX/UI (built around user behavior + funnels)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Full analytics + event tracking (clicks, forms, conversions)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>SEO strategy plan (keyword targeting + content direction)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>AI-driven research (competitors + search intent)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Landing page structuring (CTAs, copy flow, user journey)</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-purple-400">$90 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">50-75</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$4,500 - $6,750</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Growth Plan</p>
                          <p className="text-purple-400">$199.99 / mo</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 3 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                        Custom Plan
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>
                            Fully custom software built around your business workflow. 
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>
                            Secure user authhentication, employee/admin dashboards, and client portals.
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Custom database architecture for storing and managing business data.</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>API integrations (Stripe, QuickBooks, CRMs, Google, third-party platforms)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Scalable architecture deisgned to grow your business</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Role based permissions and account management</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-pink-400">$150 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">80+</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$12,000+</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Premium Plan</p>
                          <p className="text-pink-400">$499.99 / mo</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Project Estimator Modal */}
          <ProjectEstimator isOpen={isEstimatorOpen} onClose={() => setIsEstimatorOpen(false)} />
        </section>

        {/* CTA Bottom Section */}
        <motion.section
          className="px-4 sm:px-6 lg:px-8 py-20 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-[0_0_50px_rgba(168,85,247,0.15)]">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready for a new Website?
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation call and let's craft a digital experience that elevates
              your brand.
            </p>
            <button
              onClick={() => router.push('/contact')}
              className="px-10 py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white rounded-full font-bold text-lg hover:scale-105 transition-transform duration-300 shadow-lg shadow-purple-500/30"
            >
              Get Started Today
            </button>
          </div>
        </motion.section>

        <ResponsiveFooter />
      </div>
    </LazyMotion>
  );
}
